import {  t  } from "@/client/libs/dummy-i18n";
import { DotsThreeVertical, Plus, Trash } from "@phosphor-icons/react";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@stackmorph/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Helmet } from "react-helmet-async";

import { toast } from "@/client/hooks/use-toast";
import { axios } from "@/client/libs/axios";

export const AdminUsersPage = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [premiumFilter, setPremiumFilter] = useState<string>("all");
  const [selectedUser, setSelectedUser] = useState<any>(null);
  
  // Dialog states
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Form states
  const [grantType, setGrantType] = useState<"monthly" | "yearly" | "lifetime">("monthly");
  const [grantReason, setGrantReason] = useState("");
  const [newUser, setNewUser] = useState({ name: "", email: "", username: "", password: "" });

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "users", search, premiumFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (premiumFilter !== "all") params.append("isPremium", premiumFilter);

      const response = await axios.get(`/admin/users?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });
      return response.data;
    },
  });

  const createUserMutation = useMutation({
    mutationFn: async (data: any) => {
      await axios.post("/admin/users", data, {
        headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      setCreateDialogOpen(false);
      setNewUser({ name: "", email: "", username: "", password: "" });
      toast({ variant: "success", title: t`User Created`, description: t`New user account has been created.` });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      await axios.delete(`/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      toast({ variant: "success", title: t`User Deleted`, description: t`User account has been permanently deleted.` });
    },
  });

  const grantPremiumMutation = useMutation({
    mutationFn: async (userId: string) => {
      await axios.post(
        `/admin/users/${userId}/grant-premium`,
        { type: grantType, reason: grantReason },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      setGrantDialogOpen(false);
      setSelectedUser(null);
      toast({
        variant: "success",
        title: t`Premium Granted`,
        description: t`User has been granted premium access.`,
      });
    },
  });

  const revokePremiumMutation = useMutation({
    mutationFn: async (userId: string) => {
      await axios.post(
        `/admin/users/${userId}/revoke-premium`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` },
        },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      toast({ variant: "success", title: t`Premium Revoked`, description: t`Premium access has been revoked.` });
    },
  });

  return (
    <>
      <Helmet>
        <title>{t`User Management - Admin`}</title>
      </Helmet>

      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{t`User Management`}</h1>
            <p className="text-muted-foreground">{t`Manage user accounts and premium access`}</p>
          </div>
          <Button onClick={() => { setCreateDialogOpen(true); }}>
            <Plus className="mr-2" />
            {t`Create User`}
          </Button>
        </div>

        <div className="flex gap-4">
          <Input
            placeholder={t`Search by name or email...`}
            value={search}
            className="max-w-sm"
            onChange={(e) => { setSearch(e.target.value); }}
          />
          <Select value={premiumFilter} onValueChange={setPremiumFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t`All Users`}</SelectItem>
              <SelectItem value="true">{t`Premium Only`}</SelectItem>
              <SelectItem value="false">{t`Free Only`}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-4">
          {isLoading ? (
            <Card>
              <CardContent className="p-6 text-center">
                {t`Loading...`}
              </CardContent>
            </Card>
          ) : (
            data?.users?.map((user: any) => (
              <Card key={user.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{user.name}</CardTitle>
                      <CardDescription>{user.email}</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {user.isPremium ? (
                        <Badge>{t`Premium`}</Badge>
                      ) : (
                        <Badge outline>{t`Free`}</Badge>
                      )}
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost">
                            <DotsThreeVertical />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!user.isPremium && (
                            <DropdownMenuItem onClick={() => {
                              setSelectedUser(user);
                              setGrantDialogOpen(true);
                            }}>
                              {t`Grant Premium`}
                            </DropdownMenuItem>
                          )}
                          {user.isPremium && (
                            <DropdownMenuItem onClick={() => { revokePremiumMutation.mutate(user.id); }}>
                              {t`Revoke Premium`}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            className="text-error"
                            onClick={() => {
                              setSelectedUser(user);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash className="mr-2" />
                            {t`Delete User`}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4 text-sm text-muted-foreground">
                    <span>{user._count?.resumes ?? 0} {t`resumes`}</span>
                    <span>{user._count?.organizations ?? 0} {t`organizations`}</span>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Grant Premium Dialog */}
        <Dialog open={grantDialogOpen} onOpenChange={setGrantDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t`Grant Premium Access`}</DialogTitle>
              <DialogDescription>
                {t`Grant premium access to`} {selectedUser?.name}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">{t`Plan Type`}</label>
                <Select
                  value={grantType}
                  onValueChange={(value: any) => { setGrantType(value); }}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">{t`Monthly (30 days)`}</SelectItem>
                    <SelectItem value="yearly">{t`Yearly (365 days)`}</SelectItem>
                    <SelectItem value="lifetime">{t`Lifetime`}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium">{t`Reason (Optional)`}</label>
                <Input
                  className="mt-2"
                  placeholder={t`e.g., Promotion, Compensation, Test`}
                  value={grantReason}
                  onChange={(e) => { setGrantReason(e.target.value); }}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setGrantDialogOpen(false); }}>
                {t`Cancel`}
              </Button>
              <Button
                disabled={grantPremiumMutation.isPending}
                onClick={() => {
                  if (selectedUser) {
                    grantPremiumMutation.mutate(selectedUser.id);
                  }
                }}
              >
                {grantPremiumMutation.isPending ? t`Granting...` : t`Grant Premium`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create User Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t`Create New User`}</DialogTitle>
              <DialogDescription>{t`Add a new user to the system.`}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Input
                placeholder={t`Full Name`}
                value={newUser.name}
                onChange={(e) => { setNewUser({ ...newUser, name: e.target.value }); }}
              />
              <Input
                placeholder={t`Email Address`}
                value={newUser.email}
                onChange={(e) => { setNewUser({ ...newUser, email: e.target.value }); }}
              />
              <Input
                placeholder={t`Username`}
                value={newUser.username}
                onChange={(e) => { setNewUser({ ...newUser, username: e.target.value }); }}
              />
              <Input
                type="password"
                placeholder={t`Password`}
                value={newUser.password}
                onChange={(e) => { setNewUser({ ...newUser, password: e.target.value }); }}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setCreateDialogOpen(false); }}>{t`Cancel`}</Button>
              <Button 
                disabled={createUserMutation.isPending}
                onClick={() => { createUserMutation.mutate(newUser); }}
              >
                {createUserMutation.isPending ? t`Creating...` : t`Create User`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete User Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t`Delete User`}</DialogTitle>
              <DialogDescription>
                {t`Are you sure you want to delete`} {selectedUser?.name}? {t`This action cannot be undone.`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); }}>{t`Cancel`}</Button>
              <Button 
                variant="error" 
                disabled={deleteUserMutation.isPending}
                onClick={() => selectedUser && deleteUserMutation.mutate(selectedUser.id)}
              >
                {deleteUserMutation.isPending ? t`Deleting...` : t`Delete User`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
